import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { ExpandMoreIcon } from "@dotnet/telematics-design-system";

import Checkbox from "components/DesignComponents/Checkbox";
import TextLink, {
    TEXT_LINK_SIZES
} from "components/DesignComponents/TextLink";
import { getPermissionActionLabel } from "shared";

import { getPermissionCheckState } from "../../functions";
import { SinglePermissionGroupProps } from "./types";

const PERMISSION_ROW_HEIGHT = 44;

const SinglePermissionGroup = ({
    permissionGroup,
    checkedPermissions,
    isLocalPermissionCheck,
    togglePermissionCheck,
    hasNoBoxShadow
}: SinglePermissionGroupProps) => {
    const { t } = useTranslation();

    const [isExpanded, setIsExpanded] = useState(false);

    const { groupId, permissions } = permissionGroup;

    const maxHeight = isExpanded
        ? `${permissions.length * PERMISSION_ROW_HEIGHT}px`
        : "0px";

    const duration = Math.min(0.1 * permissions.length, 0.4);

    const { isAll, isPartial, isAtLeastOne, isNone } = getPermissionCheckState(
        permissions,
        checkedPermissions
    );

    useEffect(() => {
        !isLocalPermissionCheck && setIsExpanded(isAtLeastOne);
    }, [isLocalPermissionCheck, isAtLeastOne]);

    const toggleAllGroupCheck = () => {
        const updatedPermissions = isNone ? permissions : checkedPermissions;
        togglePermissionCheck(updatedPermissions);
    };

    return (
        <>
            <div
                css={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: hasNoBoxShadow
                        ? "none"
                        : "0 -1px 0 0 #E2E8F0 inset",
                    padding: "14px 24px 14px 18px"
                })}
            >
                <Checkbox
                    label={t(`Permission groups##${groupId}`)}
                    checked={isAll}
                    indeterminate={isPartial}
                    onChange={toggleAllGroupCheck}
                />

                <TextLink
                    size={TEXT_LINK_SIZES.Small}
                    icon={<ExpandMoreIcon />}
                    onClick={() => setIsExpanded(!isExpanded)}
                    css={css({
                        "& > svg": {
                            transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)"
                        }
                    })}
                >
                    {isExpanded ? t("Button##close") : t("Button##see all")}
                </TextLink>
            </div>

            <div
                css={css({
                    overflow: "hidden",
                    maxHeight,
                    opacity: isExpanded ? 1 : 0,
                    transition: `max-height ${duration}s ease, opacity ${duration}s ease`
                })}
            >
                {permissions.map(permission => (
                    <div
                        key={permission}
                        css={css({
                            display: "flex",
                            alignItems: "center",
                            marginLeft: "42px",
                            padding: "10px 24px 10px 8px",
                            boxShadow: "0 -1px 0 0 #E2E8F0 inset"
                        })}
                    >
                        <Checkbox
                            label={getPermissionActionLabel(permission, t)}
                            checked={checkedPermissions.includes(permission)}
                            onChange={() =>
                                togglePermissionCheck([permission], true)
                            }
                        />
                    </div>
                ))}
            </div>
        </>
    );
};

export default SinglePermissionGroup;
